/**
 * Selecting or loading a profile replaces the setup the user is looking at, so
 * both are navigation destinations like any tab or step: the state change runs
 * only inside the guard's commit, after the unsaved-edit decision is settled
 * and while the intent is still the one the user raised.
 */

import type { NavigationSource } from '../../../shared/lib/setupRevision';
import {
  requestGuardedNavigation,
  type NavigationCommitOptions,
} from '../../../shared/ui/navigation';
import { profileDestination } from './mappingNavigationDestinations';

/** The profile name the user recognises; the scope suffix is not part of it. */
const profileDisplayName = (profileKey: string): string | undefined =>
  profileKey.split('::')[0] || undefined;

/** Runs `commit` only once the guard has let this profile destination through. */
export const requestGuardedProfileChange = async (
  profileKey: string,
  source: NavigationSource,
  commit: () => void,
  options?: NavigationCommitOptions
): Promise<boolean> => {
  const outcome = await requestGuardedNavigation(
    profileDestination(source, profileKey, profileDisplayName(profileKey)),
    commit,
    options
  );

  return outcome === 'committed';
};

/**
 * The same contract for a load, which additionally reports whether the profile
 * came back. A blocked, cancelled or superseded intent resolves false without
 * ever starting the load.
 */
export const requestGuardedProfileLoad = async (
  profileKey: string,
  source: NavigationSource,
  load: () => Promise<boolean>,
  options?: NavigationCommitOptions
): Promise<boolean> => {
  const started: { load: Promise<boolean> | null } = { load: null };

  const committed = await requestGuardedProfileChange(
    profileKey,
    source,
    () => {
      started.load = load();
    },
    options
  );

  return committed && started.load ? started.load : false;
};
